// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { Text } from '@mantine/core';

import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { TextareaWrapped } from 'components/Mantine/TextInputWrapped';
import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';
import type { WorkOrderActionModalProps } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/workOrderActions';
import { useWorkOrders } from 'modules/Shop/WorkOrders/WorkOrdersPage/useWorkOrders';

export const CancelWorkOrderModal = ({
  workOrder,
  onWorkOrderActioned,
  onClose,
  ...props
}: WorkOrderActionModalProps) => {
  const { cancelWorkOrder } = useWorkOrders();
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);
  const canRequestCancel = canEditWorkOrder('requestCancel');
  const [canceledReason, setCanceledReason] = useState('');

  const onSubmit = async () => {
    await cancelWorkOrder(workOrder.workOrderId, canceledReason);
    onClose();
    onWorkOrderActioned();
  };

  return (
    <WrappedModal
      title={canRequestCancel ? 'Request work order cancellation' : 'Cancel work order'}
      onConfirm={onSubmit}
      onClose={onClose}
      confirmationText={canRequestCancel ? 'Request' : 'Cancel'}
      cancellationText="Go back"
      buttonColor="red"
      disabled={!canceledReason.trim()}
      {...props}
    >
      {canRequestCancel && (
        <Text>
          This work order has already been released. This cancellation request will need to be approved before it can
          proceed.
        </Text>
      )}
      <Text>
        Please provide a reason for canceling <b>{workOrder.name}</b>.
      </Text>
      <TextareaWrapped
        label="Reason"
        required
        placeholder="Required"
        value={canceledReason}
        onChange={setCanceledReason}
        maxLength={1000}
        autoFocus
        data-autofocus
      />
    </WrappedModal>
  );
};
